import { type PropsWithChildren, useRef, useState } from 'react';
import styled from 'styled-components';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';
import {
  dateFormat,
  getInputDateClampers,
  sanitizeNumericInput,
  validateAge
} from '../helpers';
import { inputAriaLabels } from './constants';

export type ItemAllowDateProps = PropsWithChildren<{
  onClick: (value: boolean) => void;
}>;

export const ItemAllowDate = ({ children, onClick }: ItemAllowDateProps) => {
  const [date, setDate] = useState({
    MM: '',
    DD: '',
    YYYY: ''
  });

  const inputDateClampers = getInputDateClampers(date);
  const inputMonth = useRef(null);
  const inputDay = useRef(null);
  const inputYear = useRef(null);

  const {
    submitButtonColor,
    textColor,
    submitButtonText,
    minimumAge,
    dateType,
    shape,
    textFont
  } = useSettings();

  const clickHandler = (e) => {
    e.preventDefault();

    const monthValue = inputMonth.current.value;
    const dayValue = inputDay.current.value;
    const yearValue = inputYear.current.value;

    onClick(validateAge(minimumAge, yearValue, monthValue, dayValue));
  };

  let localDateFormat;

  if (dateType === 'auto') {
    localDateFormat = dateFormat();
  } else {
    localDateFormat = dateType.split('/');
  }

  const inputPartRefs = {
    MM: inputMonth,
    DD: inputDay,
    YYYY: inputYear
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && inputYear.current.value.length > 3) {
      clickHandler(e);
    }
  };

  const Input = ({ inputPart, refObject, nextItem, ariaLabel }) => (
    <DateInput
      type="number"
      inputMode="numeric"
      aria-label={ariaLabel}
      onKeyDown={handleKeyPress}
      name={inputPart}
      placeholder={inputPart}
      ref={refObject}
      max={inputPart.length === 2 ? '99' : '9999'}
      maxLength={inputPart.length}
      defaultValue={date[inputPart]}
      _textColor={textColor || 'inherit'}
      _fontSize={textFont.fontSize}
      _shape={shape}
      onBlur={({ target: inputElement }) => {
        const result = date;
        inputElement.value = inputDateClampers[inputPart](inputElement.value);
        result[inputPart] = inputElement.value;

        for (const otherInputPart in inputPartRefs) {
          const otherInputValue = inputPartRefs[otherInputPart].current.value;

          if (otherInputPart === inputPart || !otherInputValue) {
            continue;
          }

          const clampedOtherInputValue =
            inputDateClampers[otherInputPart](otherInputValue);

          inputPartRefs[otherInputPart].current.value = clampedOtherInputValue;
          result[otherInputPart] = clampedOtherInputValue;
        }

        setDate(result);
      }}
      onChange={({ target: inputElement }) => {
        inputElement.value = sanitizeNumericInput(
          inputElement.value,
          inputElement.maxLength
        );

        if (inputElement.max.length === inputElement.value.length && nextItem) {
          nextItem.current.focus();
        }

        if (
          inputElement.max.length === inputElement.value.length &&
          !nextItem
        ) {
          inputElement.blur();
        }
      }}
    />
  );

  return (
    <>
      <Container>
        {localDateFormat.map((item, id) => (
          <Input
            key={id}
            inputPart={item}
            refObject={inputPartRefs[item]}
            ariaLabel={inputAriaLabels[item]}
            nextItem={inputPartRefs[localDateFormat[id + 1]] || null}
          />
        ))}
      </Container>

      {children}

      <SubmitButton
        type="button"
        onClick={clickHandler}
        _submitButtonColor={submitButtonColor}
        _fontSize={textFont.fontSize}
        _shape={shape}
      >
        {submitButtonText}
      </SubmitButton>
    </>
  );
};

const Container = styled.div.attrs({
  className: `${meta.prefix}-item-allow-date-container`
})`
  display: flex;
  justify-content: space-between;

  @media (max-width: 465px) {
    flex-wrap: wrap;
  }
`;

const DateInput = styled.input.attrs({
  className: `${meta.prefix}-item-allow-date-input`
})`
  flex: 1 1 auto;
  min-width: 0;
  padding: 16px;
  outline: none;
  box-sizing: border-box;
  line-height: 20px;
  transition: 0.3s ease all;
  border: 1px solid
    ${({ _textColor }) =>
      _textColor ? changeColorAlpha(_textColor, -0.6) : 'transparent'};
  background-color: ${({ _textColor }) =>
    _textColor ? changeColorAlpha(_textColor, -0.95) : 'transparent'};
  color: ${({ _textColor }) => _textColor};
  font-size: ${({ _fontSize }) => _fontSize}px;
  border-radius: ${({ _shape }) => (_shape ? '4px' : '0')};

  &:nth-child(2) {
    margin: 0 10px;

    @media (max-width: 465px) {
      margin: 10px 0;
    }
  }

  &::placeholder {
    color: currentColor;
    opacity: 0.5;
  }

  @media (max-width: 465px) {
    margin: 0;
  }

  &:focus {
    background-color: ${({ _textColor }) =>
      _textColor ? changeColorAlpha(_textColor, -0.9) : 'transparent'};
    border-color: ${({ _textColor }) =>
      _textColor ? changeColorAlpha(_textColor, -0.8) : 'transparent'};
  }
`;

const SubmitButton = styled.button.attrs({
  className: `${meta.prefix}-item-allow-date-submit`
})`
  padding: 12px;
  outline: none;
  background-color: ${({ _submitButtonColor }) =>
    _submitButtonColor || 'transparent'};
  cursor: pointer;
  width: 100%;
  margin-top: 32px;
  font-size: ${({ _fontSize }) => _fontSize}px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: ${({ _submitButtonColor }) =>
    _submitButtonColor
      ? getBrightInvertedColor(_submitButtonColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])
      : 'rgb(255, 255, 255)'};
  border: none;
  transition: 0.3s ease background-color;
  border-radius: ${({ _shape }) => (_shape ? '4px' : '0')};

  &:hover {
    background-color: ${({ _submitButtonColor }) =>
      _submitButtonColor
        ? changeColorAlpha(_submitButtonColor, -0.2)
        : 'transparent'};
  }
`;
